<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { storeToRefs } from 'pinia';
import MultiSelect, { MultiSelectChangeEvent } from 'primevue/multiselect';
import { useRouting, useRequestCancel, can } from '@/composables';
import { useReportStore, useReviewReportStore } from '@/store';
import ReportSubjectTitle from '@/views/Reports/components/ReportSubjectTitle.vue';
import ReviewReportTrack from './ReviewReportTrack.vue';
import ReviewReportClinicianMinutes from './ReviewReportClinicianMinutes.vue';
import ReviewReportStudents from './ReviewReportStudents.vue';
import ReviewReportExitedStudents from './ReviewReportExitedStudents.vue';
import ReviewReportAttendance from './ReviewReportAttendance.vue';
import { PERMISSIONS, EMIT } from '@/enum';

const $routing = useRouting();

const emit = defineEmits([EMIT.QUERY_PARAMS]);

const reportStore = useReportStore();

const reviewReportStore = useReviewReportStore();

const {
  filters: {
    value: { disciplines, eventTypes },
  },
} = storeToRefs(reviewReportStore);

const reportSubjectName = ref();

onMounted(() => init());

onBeforeUnmount(() => {
  useRequestCancel().requestSaverAbortAll();
  $routing.router.push({ query: {} });
});

$routing.routeChangeWatcher(async () => {
  if (isReviewReport.value) {
    reviewReportStore.queryParams = $routing.route.query;
    reviewReportStore.getEventTypes();
    reviewReportStore.getDisciplines();
    defineFilters();
    doReviewReport();
    defineReportName();
  }
});

const isReviewReport = computed<boolean>(() => $routing.route.name === 'reports.review');

const init = async () => {
  if (isReviewReport.value) {
    if (can([PERMISSIONS.ORGANIZATION_SERVICES_INDEX])) await reviewReportStore.getEventTypes();
    if (can([PERMISSIONS.ORGANIZATION_DISCIPLINES_INDEX])) await reviewReportStore.getDisciplines();
    await defineFilters();
    if (!reportStore.referrer) {
      await doReviewReport();
      await defineReportName();
      reportStore.referrer = $routing.route.name;
    }
  }
};

const doReviewReport = () => {
  const query = $routing.route.query;
  const filters = reviewReportStore.defineFilters(query);
  reviewReportStore.resetReviewReportData();
  reviewReportStore
    .getReviewReportTrack(filters)
    .then(() => doReviewReportClinicianMinutesAndStudents(filters))
    .catch(() => doReviewReportTherapyResultAndExitedStudents(filters));
};

const doReviewReportClinicianMinutesAndStudents = (filters) => {
  reviewReportStore.getReviewReportClinicianMinutes(filters);
  reviewReportStore
    .getReviewReportStudents(filters)
    .then(() => doReviewReportTherapyResultAndExitedStudents(filters))
    .catch(() => doReviewReportTherapyResultAndExitedStudents(filters));
};

const doReviewReportTherapyResultAndExitedStudents = (filters) => {
  reviewReportStore.getReviewReportTherapyResult(filters);
  reviewReportStore.getReviewReportExitedStudents(filters);
};

const defineFilters = async () => {
  const query = $routing.route.query;

  const disciplines: number[] = [];
  if (query.disciplines) {
    query.disciplines
      .toString()
      .split(',')
      .forEach((item) => disciplines.push(+item));
  }
  reviewReportStore.filters.disciplines.selected = query.disciplines ? disciplines : null;

  const eventTypes: number[] = [];
  if (query.event_types) {
    query.event_types
      .toString()
      .split(',')
      .forEach((item) => eventTypes.push(item));
  }
  reviewReportStore.filters.eventTypes.selected = query.event_types ? eventTypes : null;

  // Add back the query params for the report once it regains focus again.
  if (reviewReportStore.queryParams) {
    $routing.addQueryParams(reviewReportStore.queryParams);
  }

  emit(EMIT.QUERY_PARAMS);
};

const defineReportName = async () => (reportSubjectName.value = reportStore.clinicians || reportStore.organizations);

const onDisciplineChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('disciplines', event.value.join());
  } else {
    $routing.removeQueryParam('disciplines');
  }
};

const onEventTypeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('event_types', event.value.join());
  } else {
    $routing.removeQueryParam('event_types');
  }
};
</script>

<template>
  <div class="page-content">
    <Card>
      <template #content>
        <ReportSubjectTitle class="pt-3" title="Review" :name="reportSubjectName" :dates="reportStore.filters.dates" />
        <div class="flex flex-wrap gap-2 mt-3">
          <div v-if="can([PERMISSIONS.ORGANIZATION_DISCIPLINES_INDEX])">
            <MultiSelect
              v-model="disciplines.selected"
              :options="disciplines.options"
              :loading="disciplines.loading"
              :filter="true"
              :reset-filter-on-hide="true"
              class="w-full"
              option-label="name"
              option-value="value"
              :show-toggle-all="false"
              placeholder="All Disciplines"
              display="chip"
              @change="onDisciplineChange"
            />
          </div>
          <div v-if="can([PERMISSIONS.ORGANIZATION_SERVICES_INDEX])">
            <MultiSelect
              v-model="eventTypes.selected"
              :options="eventTypes.options"
              :loading="eventTypes.loading"
              :filter="true"
              :reset-filter-on-hide="true"
              class="w-full"
              option-label="name"
              option-value="value"
              :show-toggle-all="false"
              placeholder="All Event Types"
              display="chip"
              @change="onEventTypeChange"
            />
          </div>
        </div>

        <ReviewReportTrack />
        <div class="w-full flex flex-wrap mt-2">
          <div class="w-full flex flex-wrap gap-3">
            <ReviewReportClinicianMinutes class="min-w-half flex-1" />
            <ReviewReportStudents class="min-w-half flex-1" />
            <ReviewReportAttendance class="min-w-half flex-1" />
            <ReviewReportExitedStudents class="min-w-half flex-1" />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
    margin: 0rem -1rem;
  }

  .p-chart {
    height: 60px;
    width: 60px;

    &.attendance {
      height: 150px;
      width: 150px;
    }
  }

  .border-round {
    border-radius: 6px !important;
  }

  .border-round-top {
    border-radius: 4px 4px 0 0 !important;
  }

  .border-round-bottom {
    border-radius: 0 0 4px 4px !important;
  }
}
</style>
